// Thin fetch wrapper around the backend API.
// Auth header priority:
//   1. Local session JWT (magic-link login) — from auth-store
//   2. Entra Bearer (MSAL has cached account)
//   3. X-Sanshi-Dev-Actor (local dev fallback)
// Optionally validates response body with a Zod schema.
// Throws ApiError on non-2xx with status + parsed body for UI to render.

import type { z } from 'zod';
import { getLocalToken, useAuth } from './auth-store.ts';
import { ENTRA_ENABLED, getIdToken } from './msal.ts';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export type DevActorPreset =
  | 'admin'
  // P1: 業務 admin 但非系統管理員（Partner 職級）— 看全部+業務，碰不到系統維運。
  | 'partner'
  | 'member'
  | 'fund_member'
  | 'fund_manager'
  | 'admin:t2'
  // 2026-05-11: named presets — 每個 member 各自一個 user_id, LoginPage 用
  // 這些避免「兩個 member 都 map 到 Brandon」 bug
  | 'samson'
  | 'brandon'
  | 'allen'
  | 'shannon'
  | 'andy'
  | { lp: number };

export function actorHeaderValue(actor: DevActorPreset): string {
  return typeof actor === 'object' ? `lp:${actor.lp}` : actor;
}

// 已知 constraint name → 中文解釋。後端 raw constraint 名字對使用者沒意義；
// 至少把最常見的幾個 unique conflict 翻成讓人看得懂的說明。
const CONSTRAINT_HINTS: Record<string, string> = {
  // V057 partial unique，但 PG 報錯時還是用 index name。也順便接 V017 舊名。
  uq_commitment_active:
    '同一投資者對同一基金/專案在同一天已經有承諾紀錄（不能重複建）— 改承諾日期或刪掉舊紀錄再試。',
  commitment_management_company_id_investor_type_investo_key:
    '同一投資者對同一基金/專案在同一天已經有承諾紀錄（不能重複建）— 改承諾日期或刪掉舊紀錄再試。',
  commitment_management_company_id_investor_type_investor_id_key:
    '同一投資者對同一基金/專案在同一天已經有承諾紀錄（不能重複建）— 改承諾日期或刪掉舊紀錄再試。',
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(formatApiErrorMessage(status, body));
    this.name = 'ApiError';
  }
}

// 2026-07-02 (user spec): 錯誤碼後面一律附「人看得懂的說明」，不要只丟 [429] Error。
// 依「錯誤 key」先，其次「後端訊息」，再其次「狀態碼」對應說明。
const ERROR_HINTS: Record<string, string> = {
  account_locked:
    '登入失敗多次，帳號已暫時鎖定 15 分鐘。可用「忘記密碼」重設密碼（重設會一併解鎖）。',
  invalid_credentials: '帳號或密碼錯誤。',
  unauthenticated: '尚未登入或登入已過期，請重新登入。',
  admin_required: '此操作僅限管理員。',
  internal_required: '此操作僅限叁石成員。',
  forbidden: '沒有權限執行此操作。',
  not_found: '找不到資料（可能已被刪除）。',
  rate_limited: '操作太頻繁，請稍候約 1 分鐘再試。',
  'Too Many Requests': '操作太頻繁，請稍候約 1 分鐘再試。',
  duplicate: '資料重複，已有相同紀錄。',
  validation_failed: '資料驗證未通過，請檢查欄位。',
};
const STATUS_HINTS: Record<number, string> = {
  400: '請求格式有誤。',
  401: '尚未登入或登入已過期，請重新登入。',
  403: '沒有權限執行此操作。',
  404: '找不到資料（可能已被刪除）。',
  409: '資料衝突（已有相同紀錄）。',
  422: '資料驗證未通過。',
  429: '操作太頻繁，請稍候約 1 分鐘再試。（若因密碼多次輸入錯誤被鎖定，請改用「忘記密碼」重設，重設會一併解鎖。）',
  500: '伺服器發生錯誤，請稍後再試。',
  502: '服務暫時無法連線，請稍後再試。',
  503: '服務暫時無法使用，請稍後再試。',
  504: '連線逾時，請稍後再試。',
};

function formatApiErrorMessage(status: number, body: unknown): string {
  if (typeof body !== 'object' || body === null) {
    return `[${status}] ${STATUS_HINTS[status] ?? '發生未預期的錯誤，請稍後再試。'}`;
  }
  const b = body as { error?: unknown; details?: unknown; message?: unknown };

  // 409 conflict + constraint 名字 → 翻譯
  if (status === 409 && b.error === 'conflict' && typeof b.details === 'object' && b.details) {
    const constraint = (b.details as { constraint?: string }).constraint;
    if (constraint && CONSTRAINT_HINTS[constraint]) {
      return CONSTRAINT_HINTS[constraint];
    }
    if (constraint) {
      // 約束名只給除錯看, 不放進使用者看到的訊息。
      console.warn(`[api] 409 conflict, constraint: ${constraint}`);
      return '[409] 資料衝突，已有相同紀錄。';
    }
  }

  // Zod validation_failed — 顯示第一個 issue
  if (b.error === 'validation_failed' && Array.isArray(b.details)) {
    const first = b.details[0] as { path?: unknown[]; message?: string } | undefined;
    if (first?.message) {
      return `${first.path?.join('.')}: ${first.message}`;
    }
  }

  // Always show the code WITH a human-readable explanation. Priority:
  //   known error-key hint > backend's own message > status-code hint > raw key.
  const errorKey = typeof b.error === 'string' ? b.error : undefined;
  // 2026-07-27: 也讀 details.message。 DB 層 CHECK 錯誤 (23514) 經 errors.ts 翻譯後
  // 是 { error: '<標題>', details: { message: '<人看得懂的原因>' } } —— 先前只讀
  // b.message, 所以**整類**資料庫防呆 (期間鎖定 / 狀態機 / KYC 閘門…) 都顯示成
  // 泛用的「[400] 請求格式有誤。」, 使用者根本不知道為什麼被擋。
  // 2026-08-03: 也讀 details.reason / details.hint。 有些防呆（基金升級門檻、專案
  // 狀態機…）把原因寫在 reason、下一步寫在 hint —— 只讀 message 的話，畫面上就只
  // 剩 `[400] fund_investing_requires_fle_and_gp` 這種給機器看的字串，
  // 後端明明已經算好「缺法人實體、去哪一頁補」卻完全沒送到使用者眼前。
  const d =
    typeof b.details === 'object' && b.details !== null
      ? (b.details as { message?: unknown; reason?: unknown; hint?: unknown })
      : undefined;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  const detailMsg =
    str(d?.message) ??
    (str(d?.reason) ? [str(d?.reason), str(d?.hint)].filter(Boolean).join(' ') : undefined);
  const backendMsg =
    (typeof b.message === 'string' && b.message ? b.message : undefined) ??
    (typeof detailMsg === 'string' && detailMsg ? detailMsg : undefined);
  // error 有時是代碼 (snake_case, 不是給人看的), 有時是後端寫好的中文標題。
  // 是句子的話就一起顯示 —— 標題 + 原因才完整。
  // 2026-07-31: 大小寫都要排除。 應用層 throw 的 Error 若沒帶 code, errors.ts 會
  // fallback 成 err.name = 'Error' —— 原本的 /^[a-z0-9_]+$/ 不匹配大寫 E, 就把它
  // 當成「後端寫的中文標題」接在前面, 顯示成「[400] Error — 真正的原因」。
  const humanKey = errorKey && !/^[A-Za-z][A-Za-z0-9_]*$/.test(errorKey) ? errorKey : undefined;
  const explanation =
    (errorKey && ERROR_HINTS[errorKey]) ??
    (humanKey ? [humanKey, backendMsg].filter(Boolean).join(' — ') : backendMsg) ??
    STATUS_HINTS[status] ??
    errorKey ??
    '發生未預期的錯誤，請稍後再試。';
  return `[${status}] ${explanation}`;
}

export interface ApiOptions<T> extends Omit<RequestInit, 'body'> {
  actor: DevActorPreset;
  json?: unknown;
  schema?: z.ZodType<T>;
}

export async function api<T = unknown>(path: string, opts: ApiOptions<T>): Promise<T> {
  const { actor, json, schema, headers, ...rest } = opts;

  // Auth header strategy (in priority order):
  //   1. Local session JWT (from magic-link login) — overrides everything
  //   2. Entra ID token (MSAL has cached account)
  //   3. X-Sanshi-Dev-Actor (local dev fallback)
  const authHeader: Record<string, string> = {};
  const localToken = getLocalToken();
  if (localToken) {
    authHeader.Authorization = `Bearer ${localToken}`;
  } else if (ENTRA_ENABLED) {
    const token = await getIdToken();
    if (token) {
      authHeader.Authorization = `Bearer ${token}`;
    } else {
      authHeader['X-Sanshi-Dev-Actor'] = actorHeaderValue(actor);
    }
  } else {
    authHeader['X-Sanshi-Dev-Actor'] = actorHeaderValue(actor);
  }

  const finalHeaders: Record<string, string> = {
    ...authHeader,
    ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
    ...((headers as Record<string, string> | undefined) ?? {}),
  };
  const r = await fetch(`${BASE}${path}`, {
    ...rest,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : ((rest as RequestInit).body ?? null),
  });
  const text = await r.text();
  let body: unknown = undefined;
  if (text.length > 0) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = text;
    }
  }
  // 2026-05-22: silent-401 UX bug — if backend rejects our token as expired/
  // invalid, the previous behaviour was to throw ApiError and let React Query
  // render 0s / empty arrays. From the user's POV that looked like "slow /
  // broken dashboard" when really their session had timed out. We now clear
  // the local token + nav state and bounce to /login, but only when the
  // request actually presented a Bearer token (don't redirect dev-actor or
  // pre-login probes).
  if (r.status === 401 && authHeader.Authorization) {
    // Avoid an infinite redirect loop if we're already heading to /login.
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      try {
        useAuth.getState().clearSession();
      } catch {
        // best-effort — fall through to redirect even if zustand state is gone
      }
      // Hard nav so React Query caches in memory are also dropped.
      window.location.href = '/login?reason=session_expired';
    }
  }
  if (!r.ok) throw new ApiError(r.status, body);
  return schema ? schema.parse(body) : (body as T);
}

/**
 * 下載檔案（文件 / 附件）。 2026-08-03 使用者：「文件這邊也要可以點下去 下載」。
 *
 * 不能用 `<a href="/api/v1/documents/:id/download">` —— 那條路徑要帶 Authorization
 * header，瀏覽器的原生導航不會帶。 所以 fetch 下來轉成 blob 再觸發下載；後端回的是
 * 302 到同源的 /api/v1/files/download?t=<短效 token>，fetch 會自動跟隨（2026-10-19
 * 前是 Blob 的 SAS 網址，Google 安全瀏覽把 Blob 帳號網域標成釣魚站後改走自己網域）。
 *
 * 跟 api() 共用同一套授權優先序（本地 JWT → Entra → dev actor），401 也一樣把
 * session 清掉、導回登入頁 —— 不然下載壞掉時會看起來像「檔案有問題」而不是「登入過期」。
 *
 * 帶 `json` → 改用 POST 送出（2026-10-19 國泰薪轉檔：付款帳號不放進網址），
 * 錯誤時讀後端的 JSON 訊息（那支端點的 400 會列出哪位員工的資料要先修）。
 */
export async function downloadFile(
  path: string,
  filename: string,
  actor: DevActorPreset,
  json?: unknown,
): Promise<void> {
  const authHeader: Record<string, string> = {};
  const localToken = getLocalToken();
  if (localToken) {
    authHeader.Authorization = `Bearer ${localToken}`;
  } else if (ENTRA_ENABLED) {
    const token = await getIdToken();
    if (token) authHeader.Authorization = `Bearer ${token}`;
    else authHeader['X-Sanshi-Dev-Actor'] = actorHeaderValue(actor);
  } else {
    authHeader['X-Sanshi-Dev-Actor'] = actorHeaderValue(actor);
  }

  const r = await fetch(
    `${BASE}${path}`,
    json === undefined
      ? { headers: authHeader }
      : {
          method: 'POST',
          headers: { ...authHeader, 'content-type': 'application/json' },
          body: JSON.stringify(json),
        },
  );
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    if (r.status === 401 && authHeader.Authorization && typeof window !== 'undefined') {
      try {
        useAuth.getState().clearSession();
      } catch {
        // best-effort
      }
      window.location.href = '/login?reason=session_expired';
    }
    let body: unknown = text;
    if (json !== undefined) {
      try {
        body = JSON.parse(text);
      } catch {
        // 不是 JSON 就照原樣
      }
    }
    throw new ApiError(r.status, body);
  }
  const url = URL.createObjectURL(await r.blob());
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
